import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Mail, Paperclip, Lock, AlertTriangle, CheckCircle2, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Read-only Pre-Authorization compose modal.
 *
 * The modal shows a locked preview — Subject, To, Body (styled HTML with the
 * Helcim CTA button visible but inert) and the attached filled declaration
 * PDF. Nothing is editable. The server re-derives the Helcim link, the dollar
 * amount, and the filename at send time from the student record, so even a
 * tampered client payload can't swap the URL.
 *
 * Props:
 *   open       — controlled open flag
 *   onClose    — close callback
 *   student    — { id, full_name, email, sale_price, application_stage, ... }
 *   onSent     — fired after a successful send with { to, amount_text, sent_at }
 */
export default function PreAuthorizationModal({ open, onClose, student, onSent }) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  // Load meta each time the modal opens — we want fresh "already_sent" state.
  useEffect(() => {
    if (!open || !student?.id) return;
    let cancelled = false;
    setMeta(null);
    setShowPdf(false);
    setPdfUrl('');
    setLoading(true);
    axios
      .get(`${API}/students/${student.id}/pre-authorization/meta`)
      .then((r) => {
        if (!cancelled) setMeta(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || 'Could not load Pre-Authorization details');
          onClose?.();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, student?.id]);

  const handlePreviewPdf = async () => {
    if (showPdf) {
      setShowPdf(false);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
      return;
    }
    try {
      const res = await axios.get(
        `${API}/students/${student.id}/pre-authorization/preview`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      setShowPdf(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load PDF preview');
    }
  };

  const handleSend = async () => {
    if (!meta || !meta.price_supported || meta.already_sent) return;
    const ok = window.confirm(
      `Send the Pre-Authorization email to ${meta.student_email}?\n\n`
      + `This can only be sent ONCE per student — the button will lock after send.`
    );
    if (!ok) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/students/${student.id}/pre-authorization/send`,
        {}
      );
      toast.success(`Sent to ${data.to}`);
      onSent?.({ to: data.to, amount_text: data.amount_text, sent_at: data.sent_at });
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl('');
    setShowPdf(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="pre-auth-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>
            <Mail size={18} className="text-blue-700" />
            Pre-Authorization Email
            <Badge variant="outline" className="ml-2 text-[11px] bg-amber-50 border-amber-200 text-amber-800">
              <Lock size={11} className="mr-1" /> Read-only preview
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-xs">
            This email will be sent from <span className="font-mono text-gray-800">studyalliance@itdcanada.ca</span>.
            The Helcim link, the amount on the declaration, and the attached PDF are all generated by the server at send time and cannot be edited.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading preview…
          </div>
        )}

        {!loading && meta && !meta.price_supported && (
          <div
            className="flex items-start gap-3 p-4 bg-rose-50 border border-rose-200 rounded-lg"
            data-testid="pre-auth-unsupported-price"
          >
            <AlertTriangle size={20} className="text-rose-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-rose-900">Sale price is not in the Helcim tier list</p>
              <p className="text-rose-800 mt-1">
                This student's sale price is <span className="font-mono">C${(meta.sale_price || 0).toLocaleString()}</span>.
                The Pre-Authorization flow only supports configured tiers:
                {' '}{(meta.supported_prices || []).map(p => `C$${p.toLocaleString()}`).join(', ')}.
              </p>
              <p className="text-rose-700 mt-2 text-xs">
                Ask an admin to update the student's sale price to one of the supported values, or add a new Helcim tier.
              </p>
            </div>
          </div>
        )}

        {!loading && meta && meta.already_sent && (
          <div
            className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg"
            data-testid="pre-auth-already-sent"
          >
            <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-emerald-900">Already sent</p>
              <p className="text-emerald-800 mt-1">
                Sent to <span className="font-mono">{meta.sent_to}</span> on <span className="font-mono">{(meta.sent_at || '').slice(0, 10)}</span>
                {meta.sent_by ? <> by <span className="font-mono">{meta.sent_by}</span></> : null}.
              </p>
              <p className="text-emerald-700 mt-2 text-xs">
                Pre-Authorization is one-shot — if a resend is needed, contact an admin.
              </p>
            </div>
          </div>
        )}

        {!loading && meta && meta.price_supported && (
          <div className="space-y-3">
            {/* Compose header fields */}
            <div className="grid grid-cols-[80px_1fr] gap-2 text-sm">
              <div className="text-gray-500 font-medium">From</div>
              <div className="font-mono text-gray-800">{meta.sender_email}</div>

              <div className="text-gray-500 font-medium">To</div>
              <div className="font-mono text-gray-800" data-testid="pre-auth-to">{meta.student_email}</div>

              <div className="text-gray-500 font-medium">Reply-to</div>
              <div className="font-mono text-gray-800">{meta.reply_to}</div>

              <div className="text-gray-500 font-medium">Subject</div>
              <div className="font-medium text-gray-900" data-testid="pre-auth-subject">{meta.subject}</div>
            </div>

            {/* Amount breakdown — gives the recruiter a sanity-check line */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <p className="text-blue-900">
                <span className="font-semibold">First-quarter undertaking:</span>{' '}
                <span className="font-mono text-blue-900">{meta.amount_text}</span>
                <span className="text-blue-700 text-xs ml-1">
                  (60% of C${(meta.sale_price || 0).toLocaleString()} + C$250)
                </span>
              </p>
            </div>

            {/* Attachment pill — click to toggle inline PDF preview */}
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Paperclip size={16} className="text-gray-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{meta.filename}</p>
                  <p className="text-xs text-gray-500">PDF attachment — section 4 pre-filled with {meta.amount_text}</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-8 px-3 text-xs shrink-0"
                onClick={handlePreviewPdf}
                data-testid="pre-auth-preview-pdf"
              >
                <Eye size={14} className="mr-1" /> {showPdf ? 'Hide' : 'Preview'}
              </Button>
            </div>

            {showPdf && pdfUrl && (
              <iframe
                src={pdfUrl}
                title="Declaration preview"
                className="w-full h-[480px] border border-gray-200 rounded-lg"
                data-testid="pre-auth-pdf-iframe"
              />
            )}

            {/* Styled body preview — inert, no click handlers on the Helcim CTA */}
            <div>
              <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider mb-1">
                Email body
              </p>
              <div
                className="border border-gray-200 rounded-lg overflow-x-auto bg-white"
                style={{ pointerEvents: 'none', maxWidth: '100%' }}
                data-testid="pre-auth-body-preview"
                dangerouslySetInnerHTML={{ __html: meta.body_html }}
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={close} data-testid="pre-auth-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={
              sending
              || loading
              || !meta
              || !meta.price_supported
              || meta.already_sent
            }
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="pre-auth-send-btn"
          >
            {sending ? <><Loader2 size={14} className="mr-1 animate-spin" /> Sending…</> : <><Mail size={14} className="mr-1" /> Send Pre-Authorization</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
